import { Agent } from "../model_gen/agent_base";
import { ApiKeyOrGetter } from "./GeneralService";
import { GeneralService } from "./GeneralService";

export interface AgentUptimeMetrics {
  agent_id: number;
  agent_name: string;
  company_id: number | null;
  deployment_exists: boolean;
  deployment_age_seconds: number | null;
  deployment_age_hours: number | null;
  last_request_ms: number | null;
  time_since_last_request_seconds: number | null;
  time_since_last_request_hours: number | null;
  current_replicas: number | null;
  desired_replicas: number | null;
  time_till_scale_down_seconds: number | null;
  time_till_scale_down_hours: number | null;
  inactivity_threshold_hours: number;
}

export class AgentService {
  /**
   * Get all agents for a company
   */
  static getAgents(
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter,
    includeSubagents: boolean = true,
    includeActions: boolean = false
  ) {
    const params = new URLSearchParams();
    if (!includeSubagents) {
      params.append("include_subagents", "false");
    }
    if (includeActions) {
      params.append("include_actions", "true");
    }
    const queryString = params.toString();
    return GeneralService.get(
      `/agent/agents${queryString ? `?${queryString}` : ""}`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get all agents for an application
   */
  static getAgentsByApplication(
    applicationId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/${applicationId}/agents`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Create a new agent
   */
  static createAgent(
    agent: Partial<Agent>,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/agent/agents",
      JSON.stringify(agent),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get agent details by ID
   */
  static getAgent(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get agent details by ID with actions and sub-agents populated
   */
  static getAgentDetailed(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/detailed`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Update an agent
   */
  static updateAgent(
    agentId: number,
    updates: Partial<Agent>,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/agent/agents/${agentId}`,
      JSON.stringify(updates),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Move multiple form fields from source agent to target agent
   */
  static moveFormFields(
    sourceAgentId: number,
    targetAgentId: number,
    fieldNames: string[],
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/agent/agents/${sourceAgentId}/form/fields/move`,
      JSON.stringify({
        target_agent_id: targetAgentId,
        field_names: fieldNames,
      }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Delete an agent
   */
  static deleteAgent(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/agent/agents/${agentId}`,
      "",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Clone an agent
   */
  static cloneAgent(
    agentId: number,
    newAgentName: string,
    newApplicationName: string,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/agent/agents/${agentId}/clone`,
      JSON.stringify({
        agent_name: newAgentName,
        application_name: newApplicationName,
      }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get detailed statistics for an agent
   */
  static getAgentStatistics(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/statistics`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get all sub-agents for a parent agent
   */
  static getSubAgents(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/subagents`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get multiple agents by their IDs (for breadcrumb chains)
   */
  static getAgentsBulk(
    agentIds: number[],
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/agent/agents/bulk",
      JSON.stringify({ agent_ids: agentIds }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Add a sub-agent relationship
   */
  static addSubAgent(
    agentId: number,
    childAgentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/agent/agents/${agentId}/subagents`,
      JSON.stringify({ child_agent_id: childAgentId }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Remove a sub-agent relationship
   */
  static removeSubAgent(
    agentId: number,
    childAgentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/agent/agents/${agentId}/subagents/${childAgentId}`,
      "",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get all agent request contexts from DynamoDB
   */
  static getAgentRequestContexts(
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/agent/agents/request-contexts",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get paginated agent sessions for a specific agent
   */
  static getAgentSessions(
    agentId: number,
    page: number = 1,
    pageSize: number = 20,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/sessions?page=${page}&page_size=${pageSize}`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get DynamoDB contexts for a specific session
   */
  static getSessionContexts(
    agentId: number,
    sessionId: string,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/sessions/${encodeURIComponent(
        sessionId
      )}/contexts`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get agent session with contexts and their associated request/response data
   */
  static getAgentSessionRequests(
    agentId: number,
    sessionId: string,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/sessions/${encodeURIComponent(
        sessionId
      )}/requests`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get agent session timeline data from PostgreSQL
   */
  static getAgentSessionTimeline(
    agentId: number,
    sessionId: string,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/sessions/${encodeURIComponent(
        sessionId
      )}/timeline`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get agent task timeline data from PostgreSQL
   */
  static getAgentTaskTimeline(
    agentId: number,
    taskId: string,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/agents/${agentId}/task/${encodeURIComponent(taskId)}/timeline`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get DynamoDB contexts for specific request IDs
   */
  static getRequestContextsByRequestIds(
    agentId: number,
    requestIds: string[],
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/agent/agents/${agentId}/request-contexts`,
      JSON.stringify({ request_ids: requestIds }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get detailed routing result with rule and condition evaluations
   */
  static getRoutingResultDetail(
    agentId: number,
    routingResultId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/${agentId}/routing/analytics/result/${routingResultId}`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Enable an action (set status to ENABLED)
   */
  static enableAction(
    actionId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/agent/actions/${actionId}/enable`,
      "",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Disable an action (set status to DISABLED)
   */
  static disableAction(
    actionId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/agent/actions/${actionId}/disable`,
      "",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Enable a sub-agent relationship (set status to ENABLED)
   */
  static enableSubAgent(
    agentId: number,
    childAgentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/agent/agents/${agentId}/subagents/${childAgentId}/enable`,
      "",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Disable a sub-agent relationship (set status to DISABLED)
   */
  static disableSubAgent(
    agentId: number,
    childAgentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/agent/agents/${agentId}/subagents/${childAgentId}/disable`,
      "",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Copy an action to another agent
   */
  static copyActionToAgent(
    actionId: number,
    targetAgentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/agent/actions/${actionId}/copy`,
      JSON.stringify({ target_agent_id: targetAgentId }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Send a message to the AI assistant
   */
  static sendAssistantMessage(
    message: string,
    agentIds: number[],
    conversationHistory: Array<{ role: string; content: string }>,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/assistant/agent/message",
      JSON.stringify({
        message,
        agent_ids: agentIds,
        conversation_history: conversationHistory,
      }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Approve and execute assistant proposed changes
   */
  static approveAssistantChanges(
    proposedChanges: any[],
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/assistant/agent/approve",
      JSON.stringify({ proposed_changes: proposedChanges }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Create a new agent from assistant
   */
  static createAgentFromAssistant(
    agentData: any,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/assistant/agent/create",
      JSON.stringify({ agent_data: agentData }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Create one or more actions from assistant
   */
  static createActionFromAssistant(
    actionDataArray: any[],
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/assistant/agent/create-action",
      JSON.stringify({ action_data: actionDataArray }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Add a subagent relationship from the assistant
   */
  static addSubagentFromAssistant(
    subagentData: any,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/assistant/agent/add-subagent",
      JSON.stringify({ subagent_data: subagentData }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  static getAgentDeploymentStatus(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/agent/${agentId}/status/global`,
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  static ensureAgentReadyGlobal(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/agent/ready/global",
      JSON.stringify({ agent_id: agentId }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Get uptime metrics for all agents across all regions
   */
  static getAllAgentsUptimeMetricsGlobal(
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/agent/metrics/uptime/global",
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  /**
   * Scale down an agent deployment to 0 replicas across all regions
   */
  static scaleDownAgentGlobal(
    agentId: number,
    setLoading: (loading: boolean) => void,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/agent/scale-down/global",
      JSON.stringify({ agent_id: agentId }),
      setLoading,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
